import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/site24x7')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Site24x7 Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Site24x7: Site24x7 is a full observability suite; its 30s checks start on Enterprise Plus Web. Uptrack checks every 30s from $3/mo." },
      { property: 'og:title', content: 'Site24x7 Alternative — Uptrack' },
      { property: 'og:description', content: "Site24x7 Web Uptime is $10/mo for 25 websites at 1-min checks. Uptrack Pro is $3/mo for 50 monitors at 30s." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-site24x7.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/site24x7' },
    ],
    scripts: comparisonScripts('site24x7', "Site24x7"),
  }),
})

const data: ComparisonData = {
  competitor: "Site24x7",
  tagline: "Site24x7, from Zoho, covers APM, real user monitoring, servers, logs, and website monitoring. If you mainly need uptime checks, Uptrack offers 30-second checks from $3/mo.",
  heroDescription: "These facts come from Site24x7's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "5 monitors at 1 min" },
    { feature: "Cheapest paid plan", uptrack: "Pro $3/mo: 50 monitors at 30s", competitor: "Web Uptime $10/mo: 25 websites at 1 min", uptrackWins: true },
    { feature: "30-second checks", uptrack: "Every plan, from $3/mo", competitor: "Enterprise Plus Web, $999/mo", uptrackWins: true },
    { feature: "Status pages (cheapest paid plan)", uptrack: "5 on Pro", competitor: "3 on Web Uptime", uptrackWins: true },
    { feature: "Check locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "32 on Web Uptime" },
    { feature: "APM/RUM", uptrack: "Not available", competitor: "Available" },
    { feature: "Server monitoring", uptrack: "Not available", competitor: "Agent-based" },
    { feature: "Log management", uptrack: "Not available", competitor: "Available" },
  ],

  painPoints: [
    {
      title: "1-minute checks on entry plans",
      description: "Site24x7's free plan and Web Uptime plan check every minute. 30-second checks start on Enterprise Plus Web at $999/mo. Every Uptrack plan checks every 30 seconds.",
    },
    {
      title: "Entry price",
      description: "Site24x7 Web Uptime is $10/mo ($9/mo billed annually) for 25 websites. Uptrack Pro is $3/mo for 50 monitors.",
    },
    {
      title: "A broad platform",
      description: "Site24x7 covers APM, RUM, servers, and logs as well as uptime. If you only need uptime and browser checks, Uptrack is a smaller product.",
    },
  ],

  differentiators: [
    "30-second checks on every plan, from $3/mo for 50 monitors.",
    "Status pages on every plan: 5 on Pro, unlimited on Team and Business.",
    "A focused product: set up monitoring in minutes.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need full APM (application performance monitoring), Site24x7 includes code-level tracing, database monitoring, and transaction analysis.",
    "If you need Real User Monitoring (RUM) to track frontend performance from real browser sessions.",
    "If you need server monitoring with an installed agent (CPU, memory, disk, processes).",
    "If you need log management alongside your monitoring.",
    `If you need many check locations, Site24x7 Web Uptime checks from 32. ${MONITOR_LOCATIONS.compare.site24x7}`,
  ],
}
